// Hand-rolled ~20-LOC router (T071). window.history based, subscribes to
// popstate, supports `:param` placeholders. First-match wins.

import { type ReactNode, useSyncExternalStore } from "react";

export interface Route {
  path: string;
  element: ReactNode;
}

export interface RouterProps {
  routes: Route[];
  notFound: ReactNode;
}

export interface ActiveRoute {
  path: string;
  params: Record<string, string>;
}

/** Push a new URL onto history and notify subscribers. */
export function navigate(to: string): void {
  window.history.pushState(null, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function subscribe(cb: () => void): () => void {
  window.addEventListener("popstate", cb);
  return () => window.removeEventListener("popstate", cb);
}

function getPathname(): string {
  return window.location.pathname;
}

/** Try to match a single route pattern against the current pathname. */
function matchRoute(pattern: string, pathname: string): Record<string, string> | null {
  const pat = pattern.split("/");
  const seg = pathname.split("/");
  if (pat.length !== seg.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < pat.length; i++) {
    const p = pat[i] ?? "";
    const s = seg[i] ?? "";
    if (p.startsWith(":")) params[p.slice(1)] = s;
    else if (p !== s) return null;
  }
  return params;
}

/** Returns the active pathname + params parsed against the first matching route. */
export function useRoute(): ActiveRoute {
  const path = useSyncExternalStore(subscribe, getPathname, getPathname);
  // Without a routes context, params can't be resolved; callers reading params
  // are expected to be rendered under <Router>, which re-parses below. We still
  // expose a best-effort via reading a module-level ref set by <Router>.
  return { path, params: lastMatchedParams };
}

let lastMatchedParams: Record<string, string> = {};

export function Router({ routes, notFound }: RouterProps): ReactNode {
  const path = useSyncExternalStore(subscribe, getPathname, getPathname);
  for (const route of routes) {
    const params = matchRoute(route.path, path);
    if (params) {
      lastMatchedParams = params;
      return route.element;
    }
  }
  lastMatchedParams = {};
  return notFound;
}
